import {useEffect, useState} from "react";
import {App as AntdApp, Tooltip} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, PageHeader, SearchBar, Button} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useAnalyzeSeoMutation, useGetSeoReportQuery} from "~/reduxs/api/seoApiSlice";
import SeoResult from "../components/SeoResult";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

/**
 * Trang công cụ chấm điểm SEO: nhập domain → BE xếp hàng chấm NỀN (B2) trả report_id
 * → FE poll GET api/seo/{id} tới khi done/failed → hiển thị kết quả. Kết quả cache
 * 24h (hoặc BE chưa migrate cột status) trả THẲNG kết quả — không cần poll.
 */
function SeoCheck() {

	const {notification} = AntdApp.useApp();

	const [analyzeSeo, {isLoading}] = useAnalyzeSeoMutation();

	const [result, setResult] = useState(null);
	const [keyword, setKeyword] = useState('');
	// id báo cáo đang chấm nền — poll 3s/lần tới khi done/failed.
	const [pendingId, setPendingId] = useState(null);

	const {data: polled} = useGetSeoReportQuery(pendingId, {
		skip: !pendingId,
		pollingInterval: 3000,
		skipPollingIfUnfocused: true,
	});

	useEffect(() => {
		if (!pendingId || !polled || polled.id !== pendingId) return;

		if (polled.status === 'done') {
			setResult(polled);
			setPendingId(null);
		} else if (polled.status === 'failed') {
			notification.error({message: 'Chấm điểm thất bại', description: polled.error_message || 'Không thể craw website này.'});
			setPendingId(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [polled, pendingId]);

	const handleAnalyze = async (domain, refresh = false) => {
		try {
			setResult(null);
			const data = await analyzeSeo({domain, keyword: keyword.trim(), refresh}).unwrap();
			if (data?.queued) {
				setPendingId(data.id);   // chấm nền → chuyển sang poll
			} else {
				setResult(data);         // cache 24h / BE fallback đồng bộ → có kết quả ngay
			}
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Chấm điểm thất bại')});
		}
	};

	const busy = isLoading || !!pendingId;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-magnifying-glass-chart"
				title="Chấm điểm SEO"
				subtitle="Nhập domain để phân tích và chấm điểm SEO on-page"
				actions={
					<Button to="/seo/history" white
						leftIcon={<FontAwesomeIcon icon="fa-light fa-clock-rotate-left" />}>
						Lịch sử
					</Button>
				}
			/>

			<SearchBar
				icon="fa-light fa-globe"
				placeholder="Nhập domain, ví dụ: sikido.vn"
				buttonText="Chấm điểm"
				buttonIcon="fa-light fa-magnifying-glass-chart"
				loading={busy}
				onSubmit={handleAnalyze}
			/>

			<div className={cn('keyword-field')}>
				<label>
					<FontAwesomeIcon icon="fa-light fa-key" />
					<span>Từ khoá mục tiêu <em>(tuỳ chọn)</em></span>
					<Tooltip title="Nhập từ khoá để chấm thêm nhóm tiêu chí On-page theo từ khoá (từ khoá có trong title, description, H1, nội dung, URL…). Chỉ áp dụng khi chấm 1 TRANG cụ thể — hãy nhập URL đầy đủ thay vì domain.">
						<span style={{cursor: 'help', color: '#9ca3af'}}>
							<FontAwesomeIcon icon="fa-light fa-circle-question" />
						</span>
					</Tooltip>
				</label>
				<input
					type="text"
					value={keyword}
					disabled={busy}
					placeholder="Ví dụ: công cụ SEO — để chấm On-page theo từ khoá"
					onChange={(e) => setKeyword(e.target.value)}
				/>
			</div>

			{busy && (
				<div className={cn('loading')}>
					<span className="app-spinner" />
					<p>{pendingId
						? 'Đang craw và phân tích website trong nền (thường 1–2 phút) — bạn có thể mở tab khác, kết quả sẽ lưu vào Lịch sử…'
						: 'Đang gửi yêu cầu chấm điểm…'}</p>
				</div>
			)}

			{!busy && result && (
				<div className={cn('result-domain')}>
					<FontAwesomeIcon icon="fa-light fa-globe" /> Kết quả cho <strong>{result.domain}</strong>
					{result.meta?.keyword && <> — từ khoá: <strong>{result.meta.keyword}</strong></>}
					{result.cached && (
						<span style={{marginLeft: 10, display: 'inline-flex', alignItems: 'center', gap: 8}}>
							<Tooltip title={`Tái dùng kết quả chấm lúc ${result.cached_at} (trong 24h) — không trừ lượt kiểm tra.`}>
								<span style={{color: '#2563eb', fontSize: 13, cursor: 'help'}}>
									<FontAwesomeIcon icon="fa-light fa-clock-rotate-left" /> Kết quả lưu lúc {result.cached_at}
								</span>
							</Tooltip>
							<Button small white onClick={() => handleAnalyze(result.domain, true)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-rotate-right" />}>
								Chấm lại
							</Button>
						</span>
					)}
				</div>
			)}

			{/* Lần đầu vào trang (chưa chấm lần nào) — hướng dẫn thay vì cột kết quả trống trơn. */}
			{!busy && !result && (
				<div className={cn('first-run')}>
					<FontAwesomeIcon icon="fa-light fa-magnifying-glass-chart" />
					<h3>Nhập domain hoặc URL để bắt đầu</h3>
					<p>Công cụ sẽ tải trang, phân tích và chấm điểm SEO on-page. Bạn sẽ nhận được:</p>
					<ul>
						<li>Điểm tổng 0–100 + xếp hạng A–F, chia theo 7 nhóm tiêu chí (Kỹ thuật, Meta, Mạng xã hội, Nội dung, Hình ảnh, Cấu trúc, Bảo mật).</li>
						<li>Danh sách tiêu chí đạt / chưa đạt kèm hướng khắc phục cụ thể.</li>
						<li>Xem trước trang trên Google (SERP), Facebook và X.</li>
						<li>Nhập thêm <strong>từ khoá mục tiêu</strong> + URL 1 trang cụ thể để chấm On-page theo từ khoá.</li>
					</ul>
				</div>
			)}

			{!busy && <SeoResult result={result} />}
		</div>
	);
}

export default SeoCheck;
